import {
  Button,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  NativeSelect,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/reduxHooks";
import { handleGetCompanies, handleDeleteRole as handleDeleteRoleApi } from "../../../api/mosaic/accounts";
import { setCompanyData } from "../../../store/mosaic/companySlice";
import { IPermissionObj, IRoles } from "./interface";
import RolesModal from "./RolesModal";
import { ICreateUpdateRole } from "../../../api/mosaic/interface";
import {
  handleCreateRolesApi,
  handleGetRoles,
  handleUpdateRolesApi,
} from "../../../api/mosaic/roles";
import PermissionView from "./PermissionView";
import { useConfirmDialog } from "../../shared/ConfirmDialogProvider";
import { useToast } from "../../shared/Toast";
import SearchBar from "../../shared/SearchBar";
import SortableTableCell from "../../shared/SortableTableCell";
import { useSortable } from "../../shared/useSortable";
import { editButtonSx, deleteButtonSx, addButtonSx } from "../../shared/buttonStyles";
import PageHeader from "../../shared/PageHeader";
import theme from "../../../utils/theme";
import PageCard from "../../shared/PageCard";

const defaultFormData = {
  roleName: "",
  companyId: 0,
  permissionsObj: [],
};

const RolesComponent: React.FC = () => {
  const [open, setOpenModal] = React.useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [roles, setRoles] = useState<IRoles[]>([]);
  const user = useAppSelector((state) => state.userSlice.user);
  const companyData = useAppSelector((state) => state.companySlice.companyData);
  const dispatch = useAppDispatch();
  const { confirm } = useConfirmDialog();
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [triggerAddPermission, setTriggerAddPermission] = useState(false);
  const [formData, setFormData] = useState<ICreateUpdateRole>(defaultFormData);
  const [selectedTab, setSelectedTab] = useState(0);

  const handleClose = () => {
    setOpenModal(false);
    setFormData(defaultFormData);
  };

  const refreshRoles = async () => {
    const updatedRoles = await handleGetRoles();
    setRoles(updatedRoles);
  };

  const handleOnUpdateRole = async () => {
    try {
      setIsLoading(true);
      await handleUpdateRolesApi(formData);
      await refreshRoles();
      setIsLoading(false);
      setOpenModal(false);
      setFormData(defaultFormData);
      showSuccess("Role updated successfully");
    } catch (err) {
      setIsLoading(false);
      showError("Error updating role");
      console.error(err);
    }
  };

  const handleOnCreateRole = async () => {
    try {
      setIsLoading(true);
      await handleCreateRolesApi(formData);
      await refreshRoles();
      setIsLoading(false);
      setOpenModal(false);
      setFormData(defaultFormData);
      showSuccess("Role created successfully");
    } catch (err) {
      setIsLoading(false);
      showError("Error creating role");
      console.error(err);
    }
  };

  const handleOnChange = (key: string, value: string) => {
    setFormData({
      ...formData,
      [key]: key === "companyId" ? parseInt(value) : value,
    });
  };

  const handleDeleteRole = async (id: number) => {
    const confirmed = await confirm({ title: "Delete Role", message: "Are you sure you want to delete this role?", confirmText: "Delete" });
    if (!confirmed) return;
    try {
      await handleDeleteRoleApi(id);
      setRoles(roles.filter((role) => role.id !== id));
      showSuccess("Role deleted successfully");
    } catch (err) {
      showError("Error deleting role");
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        const [roles, companies] = await Promise.all([
          handleGetRoles(),
          handleGetCompanies(),
        ]);
        setRoles(roles);
        dispatch(setCompanyData(companies.data.data));
        setIsLoadingData(false);
      } catch (err) {
        setIsLoadingData(false);
        console.error(err);
      }
    };
    if (user) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoading, dispatch]);

  const filteredRoles = roles.filter((row) => {
    const term = search.toLowerCase();
    const matchesSearch =
      row.name.toLowerCase().includes(term) ||
      row.company?.name?.toLowerCase().includes(term);
    const matchesCompany = !companyFilter || row.company?.id?.toString() === companyFilter;
    return matchesSearch && matchesCompany;
  });

  const { sortedData: sortedRoles, sortConfig, requestSort } = useSortable(filteredRoles, "id", "desc");

  const renderRolesView = () => (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <PageCard>
        {isLoadingData ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3 }}>
              <Box sx={{ flex: 1, "& .MuiTextField-root": { mb: 0 } }}>
                <SearchBar value={search} onChange={setSearch} placeholder="Search roles..." />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <NativeSelect
                    value={companyFilter}
                    input={<OutlinedInput sx={{ borderRadius: "8px", height: 40 }} />}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                  >
                    <option value="">All Companies</option>
                    {companyData.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </NativeSelect>
                </FormControl>
                {companyFilter && (
                  <IconButton size="small" onClick={() => setCompanyFilter("")} sx={{ color: "#878787" }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
            <TableContainer sx={{ flex: 1, overflow: "auto" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <SortableTableCell label="Role Name" sortKey="name" active={sortConfig?.key === "name"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} />
                    <SortableTableCell label="Company" sortKey="company.name" active={sortConfig?.key === "company.name"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} />
                    <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Permissions</TableCell>
                    <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRoles.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: "#878787" }}>
                        No roles found
                      </TableCell>
                    </TableRow>
                  )}
                  {sortedRoles.map((row, index) => (
                    <TableRow hover key={index}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.company?.name}</TableCell>
                      <TableCell>
                        {row.permissionsObj && row.permissionsObj.length > 0
                          ? row.permissionsObj.map((p: IPermissionObj, i: number) => (
                              <Chip key={i} label={p.permissionName} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))
                          : <Chip label="None assigned" size="small" variant="outlined" />
                        }
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <Button
                          variant="contained"
                          onClick={() => {
                            setOpenModal(true);
                            setIsNewForm(false);
                            setFormData({
                              roleName: row.name,
                              companyId: row.company.id,
                              permissionsObj: row.permissionsObj,
                              roleId: row.id,
                            });
                          }}
                          sx={editButtonSx}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => handleDeleteRole(row.id)}
                          sx={deleteButtonSx}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </PageCard>

      {open && (
        <RolesModal
          openModal={open}
          handleClose={handleClose}
          handleOnChange={handleOnChange}
          handleOnUpdateRole={handleOnUpdateRole}
          handleOnCreateRole={handleOnCreateRole}
          isNewForm={isNewForm}
          formData={formData}
          setFormData={setFormData}
          isLoading={isLoading}
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <PageHeader
        title="Roles & Permissions"
        subtitle={selectedTab === 0 ? "Manage roles and their permissions" : "Manage permissions"}
      >
        {selectedTab === 0 ? (
          <Button
            variant="contained"
            sx={addButtonSx}
            onClick={() => {
              setOpenModal(true);
              setIsNewForm(true);
            }}
          >
            Add new Role
          </Button>
        ) : (
          <Button
            variant="contained"
            sx={addButtonSx}
            onClick={() => setTriggerAddPermission(true)}
          >
            Add new Permission
          </Button>
        )}
      </PageHeader>
      <Tabs
        value={selectedTab}
        onChange={(_e, val) => setSelectedTab(val)}
        sx={{
          mb: 2,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 500,
            fontSize: "15px",
          },
          "& .Mui-selected": {
            color: "#28B2AC !important",
          },
          "& .MuiTabs-indicator": {
            backgroundColor: theme.palette.brand.primary,
          },
        }}
      >
        <Tab label="Roles" />
        <Tab label="Permissions" />
      </Tabs>

      {selectedTab === 0 ? renderRolesView() : (
        <PermissionView
          isNewForm={isNewForm}
          setIsNewForm={setIsNewForm}
          isLoadingData={isLoadingData}
          setIsLoadingData={setIsLoadingData}
          selectedView="permissions_view"
          openAddModal={triggerAddPermission}
          onAddModalOpened={() => setTriggerAddPermission(false)}
        />
      )}
    </Box>
  );
};

export default RolesComponent;
